/**
 * Employer lookup hooks (FND-021, PAC-020).
 *
 * Wraps the Employer/Match service HTTP surface:
 *   - GET /api/v1/employers/search?q=&cursor=  → Page<EmployerMatch> (fuzzy auto-suggest)
 *   - GET /api/v1/employers/{id}               → Employer (detail)
 *   - GET /api/v1/employers/{id}/contacts      → EmployerContact[]
 *
 * The search hook is debounced so type-ahead does not fire a request on every
 * keystroke, and stale responses are discarded so the suggestion list always
 * reflects the latest query.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { BenefitPackage, Employer, EmployerContact, Page } from '../api/types';

/**
 * A scored employer suggestion returned by the auto-suggest API (PAC-020).
 * Mirrors the backend `EmployerMatch` wrapper, which inlines the most-recent
 * benefit package so a suggestion card can show recency/ESI without a second
 * round-trip. The wire `Employer` is a superset of the shared DTO; we type it
 * as the shared {@link Employer} plus the extra match-relevant fields.
 */
export interface EmployerMatch {
  employer: Employer;
  /** Fuzzy-match relevance score (0..1). */
  score: number;
  /** Most-recent benefit package for the employer, if any. */
  benefit_package?: BenefitPackage | null;
}

/** Default debounce window (ms) for type-ahead search. */
const SEARCH_DEBOUNCE_MS = 250;

interface UseEmployerSearchResult {
  query: string;
  setQuery: (q: string) => void;
  suggestions: EmployerMatch[];
  isSearching: boolean;
  error: ApiError | null;
}

/**
 * Debounced fuzzy employer search for the type-ahead box.
 *
 * Returns the live `query`, a `setQuery` setter, the ranked `suggestions`, and
 * loading/error state. An empty/whitespace query clears suggestions without
 * hitting the API.
 */
export function useEmployerSearch(debounceMs: number = SEARCH_DEBOUNCE_MS): UseEmployerSearchResult {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<EmployerMatch[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // Monotonically increasing request id so out-of-order responses are dropped.
  const requestSeq = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestions([]);
      setError(null);
      setIsSearching(false);
      return;
    }

    const seq = ++requestSeq.current;
    setIsSearching(true);
    const timer = setTimeout(() => {
      apiClient
        .get<Page<EmployerMatch>>(`/employers/search?q=${encodeURIComponent(trimmed)}`)
        .then((page) => {
          if (seq !== requestSeq.current) return; // a newer query superseded this one
          setSuggestions(page.items);
          setError(null);
        })
        .catch((err: unknown) => {
          if (seq !== requestSeq.current) return;
          setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
          setSuggestions([]);
        })
        .finally(() => {
          if (seq === requestSeq.current) setIsSearching(false);
        });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  return { query, setQuery, suggestions, isSearching, error };
}

interface UseEmployerDetailResult {
  employer: Employer | null;
  contacts: EmployerContact[];
  isLoading: boolean;
  error: ApiError | null;
  load: (employerId: string) => void;
  clear: () => void;
}

/**
 * Loads an employer's detail record and contact cards on demand.
 *
 * `load(id)` fetches `GET /employers/{id}` and `GET /employers/{id}/contacts`
 * in parallel; `clear()` resets the panel.
 */
export function useEmployerDetail(): UseEmployerDetailResult {
  const [employer, setEmployer] = useState<Employer | null>(null);
  const [contacts, setContacts] = useState<EmployerContact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const requestSeq = useRef(0);

  const load = useCallback((employerId: string) => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    Promise.all([
      apiClient.get<Employer>(`/employers/${encodeURIComponent(employerId)}`),
      apiClient
        .get<EmployerContact[]>(`/employers/${encodeURIComponent(employerId)}/contacts`)
        .catch(() => [] as EmployerContact[]),
    ])
      .then(([detail, detailContacts]) => {
        if (seq !== requestSeq.current) return;
        setEmployer(detail);
        setContacts(detailContacts);
      })
      .catch((err: unknown) => {
        if (seq !== requestSeq.current) return;
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setEmployer(null);
        setContacts([]);
      })
      .finally(() => {
        if (seq === requestSeq.current) setIsLoading(false);
      });
  }, []);

  const clear = useCallback(() => {
    requestSeq.current++;
    setEmployer(null);
    setContacts([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return { employer, contacts, isLoading, error, load, clear };
}
